import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AddUserModal from "./AddUserModal";
import { AddIcon } from "../../assets/icons";
import { Api } from "../../network/api";
import {
  setUsers,
  setUsersLoading,
  setUsersError,
  clearUsersError,
} from "../../redux/slice/globalSlice";

const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { users, usersLoading, usersError } = useSelector(
    (state) => state.global
  );
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [dispatch]);

  const fetchUsers = async () => {
    try {
      dispatch(setUsersLoading(true));
      dispatch(clearUsersError());

      const response = await Api.getUsers();
      const sortedUsers =
        response?.data
          ?.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.created_at || a.created);
            const dateB = new Date(b.createdAt || b.created_at || b.created);
            return dateB - dateA;
          })
          .slice(0, 10) || [];

      dispatch(setUsers(sortedUsers));
      dispatch(setUsersLoading(false));
    } catch (error) {
      console.error("Error fetching users:", error);
      dispatch(setUsersError(error.response?.data?.detail || "Failed to fetch users"));
      dispatch(setUsersLoading(false));
    }
  };

  const handleAddUserSuccess = (newUser) => {
    fetchUsers();
    console.log("User added successfully!", newUser);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row md:items-center space-y-4 lg:space-y-0 justify-between">
            <div>
              <h1 className="md:text-2xl text-xl font-extrabold text-gray-900">
                Dashboard
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                Welcome to your Calbar Admin Dashboard. Manage users,
                submissions, and flagged content from here.
              </p>
            </div>
            <button
              onClick={() => setIsAddUserModalOpen(true)}
              className="bg-gradient-to-r from-red-600 to-red-800 text-white px-6 py-3 rounded-2xl font-medium shadow-lg hover:from-red-700 hover:to-red-900 hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center md:justify-start space-x-2"
            >
              <AddIcon />
              <span className="text-sm font-semibold">Add User/Admin</span>
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <button
              className="bg-blue-50 p-4 rounded-lg border border-blue-200 hover:bg-blue-100 transition-all duration-200 transform hover:scale-105"
              onClick={() => navigate("/users")}
            >
              <h3 className="text-md md:text-lg font-semibold text-blue-900">Users</h3>
              <p className="text-blue-600 text-sm">Manage application users</p>
            </button>
            <button
              className="bg-green-50 p-4 rounded-lg border border-green-200 hover:bg-green-100 transition-all duration-200 transform hover:scale-105"
              onClick={() => navigate("/modal-answer")}
            >
              <h3 className="text-md md:text-lg font-semibold text-green-900">
                Model Answer
              </h3>
              <p className="text-green-600 text-sm">Handle modal responses</p>
            </button>
            <button
              className="bg-purple-50 p-4 rounded-lg border border-purple-200 hover:bg-purple-100 transition-all duration-200 transform hover:scale-105"
              onClick={() => navigate("/submissions")}
            >
              <h3 className="text-md md:text-lg font-semibold text-purple-900">
                Submissions
              </h3>
              <p className="text-purple-600 text-sm">Review user submissions</p>
            </button>
            <button
              className="bg-red-50 p-4 rounded-lg border border-red-200 hover:bg-red-100 transition-all duration-200 transform hover:scale-105"
              onClick={() => navigate("/flagged")}
            >
              <h3 className="text-md md:text-lg font-semibold text-red-900">
                Flagged Essays
              </h3>
              <p className="text-red-600 text-sm">Review flagged content</p>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Recent Users</h2>
          <p className="text-gray-600 text-sm mt-1">
            List of users and admins in the system
          </p>
        </div>

        {usersLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading users...</span>
          </div>
        )}

        {usersError && !usersLoading && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error loading users
            </h3>
            <p className="text-gray-600 mb-4">{usersError}</p>
            <button
              onClick={fetchUsers}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {!usersLoading && !usersError && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id || user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-6 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {user.name || user.fullName || user.username || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.email || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          (user.is_admin === true
                            ? "Admin"
                            : "User"
                          ).toLowerCase() === "admin"
                            ? "bg-red-100 text-red-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {user.is_admin === true ? "Admin" : "User"}
                      </span>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(
                        user.createdAt || user.created_at || user.created
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!usersLoading && !usersError && users.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No users found
            </h3>
            <p className="text-gray-600 mb-6">
              Get started by adding your first user or admin.
            </p>
            <button
              onClick={() => setIsAddUserModalOpen(true)}
              className="bg-gradient-to-r from-red-600 to-red-800 text-white px-6 py-3 rounded-2xl font-medium shadow-lg hover:from-red-700 hover:to-red-900 hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              Add First User
            </button>
          </div>
        )}
      </div>

      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={handleAddUserSuccess}
      />
    </div>
  );
};

export default Home;
